import { useState, useEffect, useCallback, useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { Search, Loader2, User, Bot, Palette, Zap, Building2, X, Inbox, Check, Key, GitBranch, Sparkles } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { cn } from '@/lib/utils'
import { LLMSettings } from './settings/llm-settings'
import { UISettings } from './settings/ui-settings'
import { FeatureSettings } from './settings/feature-settings'
import { ChatSkillsSettings } from './settings/chat-skills-settings'
import { AccountSettings } from './settings/account-settings'
import { OrganizationSettings } from './settings/organization-settings'
import { InboxSettings } from './settings/inbox-settings'
import { APIAccessSettings } from './settings/api-access-settings'
import { SecretsSettings } from './settings/secrets-settings'
import { GitSourcesSettings } from './git-sources-settings'
import { useAuth } from '@/providers/auth-provider'
import { useWorkspace } from '@/providers/workspace-provider'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import {
  buildWorkspaceSearchHits,
  filterSettingsSearchHits,
  type SettingsSearchHit,
} from '@/lib/settings-nav'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface SettingsPanelProps {
  form: UseFormReturn<any>
  onSubmit?: (values: any) => Promise<boolean>
  onClose?: () => void
  /** When set, select this tab on mount (e.g. 'organizations') */
  defaultTab?: string | null
  /** When set with Workspaces tab, open this organization detail */
  focusOrganizationId?: string | null
  /** When set with Account tab, scroll to section (e.g. password) */
  focusSection?: string | null
}

type NavItem = {
  id: string
  label: string
  icon: React.ReactNode
  group?: string
}

const FORM_TABS = ['llm', 'ui', 'features']

export function SettingsPanel({ form, onSubmit, onClose, defaultTab, focusOrganizationId, focusSection }: SettingsPanelProps) {
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [navOrgId, setNavOrgId] = useState<string | null>(focusOrganizationId ?? null)
  const [navSection, setNavSection] = useState<string | null>(focusSection ?? null)
  /** Bump to remount Workspaces so sticky extension pages do not survive a top-level tab click. */
  const [orgSettingsKey, setOrgSettingsKey] = useState(0)
  const { isAuthConfigured } = useAuth()
  const { workspaces, activeWorkspaceIds } = useWorkspace()

  useEffect(() => {
    setNavOrgId(focusOrganizationId ?? null)
  }, [focusOrganizationId])

  useEffect(() => {
    setNavSection(focusSection ?? null)
  }, [focusSection])

  const handleSave = useCallback(async () => {
    if (!onSubmit) return
    setSaveStatus('saving')
    try {
      const values = form.getValues()
      const ok = await onSubmit(values)
      setSaveStatus(ok ? 'saved' : 'error')
      if (ok) {
        setTimeout(() => setSaveStatus('idle'), 2500)
      }
    } catch {
      setSaveStatus('error')
    }
  }, [onSubmit, form])

  const showSaveButton = activeTab && FORM_TABS.includes(activeTab)

  const reportSaved = useCallback(() => {
    setSaveStatus('saved')
    setTimeout(() => setSaveStatus('idle'), 2500)
  }, [])

  useEffect(() => {
    loadSettings(defaultTab)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Set initial tab after auth state is known
  useEffect(() => {
    if (!loading && activeTab === null) {
      setActiveTab(defaultTab || (isAuthConfigured ? 'account' : 'llm'))
    }
  }, [loading, isAuthConfigured, defaultTab, activeTab])

  // When defaultTab changes to a new value (e.g. deep-link from "Manage Workspaces"),
  // jump to that tab immediately regardless of current activeTab
  useEffect(() => {
    if (defaultTab && !loading) {
      setActiveTab(defaultTab)
    }
  }, [defaultTab]) // eslint-disable-line react-hooks/exhaustive-deps

  const applySettingsResponse = (data: { success?: boolean; settings?: unknown }) => {
    if (data.success && data.settings) {
      form.reset(data.settings)
    }
  }

  const loadSettings = async (initialTab?: string | null) => {
    const needsFormSettings = !initialTab || FORM_TABS.includes(initialTab)
    if (!needsFormSettings) {
      setLoading(false)
      try {
        const response = await fetchWithAuth(`/api/settings`)
        const data = await response.json()
        applySettingsResponse(data)
      } catch (error) {
        console.error('Failed to load settings:', error)
      }
      return
    }

    try {
      const response = await fetchWithAuth(`/api/settings`)
      const data = await response.json()
      applySettingsResponse(data)
    } catch (error) {
      console.error('Failed to load settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const navItems: NavItem[] = [
    ...(isAuthConfigured ? [{ id: 'account', label: 'Account', icon: <User className="h-4 w-4" />, group: 'User' }] : []),
    { id: 'organizations', label: 'Workspaces', icon: <Building2 className="h-4 w-4" />, group: 'User' },
    { id: 'api-access', label: 'API Access', icon: <Key className="h-4 w-4" />, group: 'Access & Credentials' },
    { id: 'secrets', label: 'Secrets', icon: <Key className="h-4 w-4" />, group: 'Access & Credentials' },
    { id: 'git-sources', label: 'Git Sources', icon: <GitBranch className="h-4 w-4" />, group: 'Access & Credentials' },
    { id: 'llm', label: 'AI & LLM', icon: <Bot className="h-4 w-4" />, group: 'App' },
    { id: 'inbox', label: 'Inbox', icon: <Inbox className="h-4 w-4" />, group: 'App' },
    { id: 'chat-skills', label: 'Chat Skills', icon: <Sparkles className="h-4 w-4" />, group: 'App' },
    { id: 'ui', label: 'UI Preferences', icon: <Palette className="h-4 w-4" />, group: 'App' },
    { id: 'features', label: 'Features', icon: <Zap className="h-4 w-4" />, group: 'App' },
  ]

  const searchHits = useMemo(() => {
    const tabHits: SettingsSearchHit[] = navItems.map((item) => ({
      id: `tab-${item.id}`,
      kind: 'tab' as const,
      tab: item.id,
      label: item.label,
      keywords: [item.label.toLowerCase(), item.id],
    }))
    return filterSettingsSearchHits([...tabHits, ...buildWorkspaceSearchHits()], searchQuery)
  }, [searchQuery, isAuthConfigured]) // navItems identity changes; auth gate is enough

  const resolveOrgId = useCallback(() => {
    return (
      navOrgId ||
      focusOrganizationId ||
      activeWorkspaceIds?.[0] ||
      workspaces?.[0]?.id ||
      null
    )
  }, [navOrgId, focusOrganizationId, activeWorkspaceIds, workspaces])

  const applySearchHit = (hit: SettingsSearchHit) => {
    if (hit.kind === 'tab') {
      setActiveTab(hit.tab)
      setNavSection(null)
    } else if (hit.kind === 'workspace-section') {
      setActiveTab('organizations')
      const orgId = resolveOrgId()
      if (orgId) setNavOrgId(orgId)
      // Clear first so re-selecting the same hit re-applies (focus effect is value-gated).
      setNavSection(null)
      window.setTimeout(() => setNavSection(hit.section), 0)
    } else if (hit.kind === 'extension') {
      setActiveTab('organizations')
      const orgId = resolveOrgId()
      if (orgId) setNavOrgId(orgId)
      setNavSection(null)
      window.setTimeout(() => setNavSection(`extension:${hit.extensionId}`), 0)
    }
    setSearchQuery('')
  }

  const selectTopLevelTab = (tabId: string) => {
    setActiveTab(tabId)
    // Drop deep-link residue so Workspaces does not reopen Google Drive / extension pages.
    setNavSection(null)
    setNavOrgId(null)
    if (tabId === 'organizations') {
      setOrgSettingsKey((k) => k + 1)
    }
  }

  const handleFocusSectionConsumed = useCallback(() => {
    setNavSection(null)
  }, [])

  const handleLeaveWorkspaceList = useCallback(() => {
    setNavOrgId(null)
    setNavSection(null)
  }, [])

  const groups = Array.from(new Set(navItems.map((i) => i.group)))

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <Form {...form}>
      <div className="h-full flex overflow-hidden">

        {/* Left sidebar nav - default --ws-color is primary (same as Browse / mode panel). */}
        <div className="w-56 shrink-0 flex flex-col bg-sidebar overflow-y-auto ws-nav-section">
          {/* Title */}
          <div className="px-3 pt-4 pb-2">
            <p className="text-base font-semibold text-foreground">Settings</p>
          </div>

          {/* Search - destination jump index */}
          <div className="px-3 pb-2 relative">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search settings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm bg-background/50"
                data-testid={testIds.settings.searchInput}
              />
            </div>
            {searchQuery.trim() && searchHits.length > 0 && (
              <ul
                className="absolute left-3 right-3 z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
                data-testid={testIds.settings.searchResults}
              >
                {searchHits.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-accent"
                      data-testid={testIds.settings.searchResult(hit.id)}
                      onClick={() => applySearchHit(hit)}
                    >
                      <span className="font-medium text-foreground">{hit.label}</span>
                      <span className="text-muted-foreground ml-1.5">
                        {hit.kind === 'tab'
                          ? 'Tab'
                          : hit.kind === 'workspace-section'
                            ? 'Workspace'
                            : 'Extension'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {searchQuery.trim() && searchHits.length === 0 && (
              <p className="absolute left-3 right-3 z-20 mt-1 rounded-md border border-border bg-popover px-2.5 py-2 text-[11px] text-muted-foreground shadow-md">
                No matching settings
              </p>
            )}
          </div>

          {/* Nav groups */}
          <nav className="flex-1 p-2 space-y-4">
            {groups.map(group => (
              <div key={group}>
                <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                  {group}
                </p>
                <div className="space-y-0.5">
                  {navItems.filter(i => i.group === group).map(item => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => selectTopLevelTab(item.id)}
                      className={cn(
                        'ws-nav-item flex min-h-9 w-full items-center gap-2.5 py-2 text-left text-[13px] font-medium leading-snug select-none pl-[0.5em]',
                        activeTab === item.id
                          ? 'ws-nav-item--active text-sidebar-foreground'
                          : 'text-sidebar-foreground/70'
                      )}
                    >
                      <span className="shrink-0 opacity-70">{item.icon}</span>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>

        {/* Right content area - lighter than sidebar for clear contrast */}
        <div className="flex-1 flex flex-col overflow-hidden bg-muted/60">
          {/* Right pane header: section title + save feedback + actions */}
          <div className="flex items-center justify-between gap-4 px-8 py-3.5 shrink-0 bg-muted/30">
            <span className="text-base font-medium text-foreground">
              {navItems.find(i => i.id === activeTab)?.label ?? ''}
            </span>
            <div className="flex items-center gap-3">
              {saveStatus === 'saved' && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground animate-fade-in">
                  <Check className="h-3.5 w-3.5 text-green-500" />
                  Settings saved
                </span>
              )}
              {showSaveButton && (
                <Button
                  size="sm"
                  disabled={saveStatus === 'saving'}
                  onClick={handleSave}
                >
                  {saveStatus === 'saving' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Save'
                  )}
                </Button>
              )}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  aria-label="Close settings"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Workspaces owns its own sticky crumbs + scroll; other tabs use padded scroll */}
          {activeTab === 'organizations' ? (
            <div className="flex-1 overflow-hidden min-h-0">
              <OrganizationSettings
                key={orgSettingsKey}
                onSaved={reportSaved}
                focusOrganizationId={navOrgId ?? focusOrganizationId}
                focusSection={navSection ?? focusSection}
                onFocusSectionConsumed={handleFocusSectionConsumed}
                onLeaveWorkspaceList={handleLeaveWorkspaceList}
              />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-2xl px-8 py-6">
                {activeTab === 'account' && isAuthConfigured && (
                  <AccountSettings focusSection={focusSection} />
                )}
                {activeTab === 'api-access' && <APIAccessSettings />}
                {activeTab === 'secrets' && <SecretsSettings />}
                {activeTab === 'git-sources' && <GitSourcesSettings />}
                {activeTab === 'llm' && <LLMSettings form={form} />}
                {activeTab === 'inbox' && <InboxSettings />}
                {activeTab === 'chat-skills' && <ChatSkillsSettings onClose={onClose} />}
                {activeTab === 'ui' && <UISettings form={form} />}
                {activeTab === 'features' && <FeatureSettings form={form} />}
              </div>
            </div>
          )}
        </div>

      </div>
    </Form>
  )
}
